<template>
  <el-col :span="24" id="list-view" v-loading.body="changingPage && !navigating" element-loading-text="Loading...">
    <ListViewHeader
      title="DEPOSITS"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="'NewPp'"
    />
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="3"
        :autoApply="false"
        clearAction="clearPpState"
        clearMetadataAction="resetPpMeta"
        searchAction="fetchPpsWithQuery"
        @filterCleared="() => changePage(1)"
        @doneFiltering="() => { currentPage = 1 }">
        <template v-for="(f, i) in filters" :slot="`${f.type}${i}`" slot-scope="props">
          <component :key="i" :is="f.type" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col>
        <el-row type="flex">
          <el-table
            v-if="meta.count && !changingPage"
            id="pps-table"
            ref="ppsTable"
            :data="pps"
            border
            style="width: 100%; z-index: 0;"
            :maxHeight="tableHeight"
            :default-sort="{ prop: 'ledger', order: 'descending' }"
            @select="handleSelectionChange"
            @select-all="handleSelectionChange">
            <el-table-column type="selection" width="55" align="center"></el-table-column>
            <el-table-column prop="ledger" label="ID" align="center" width="100">
              <template slot-scope="scope">
                <el-row type="flex" justify="center">
                  <router-link :to="{name: 'EditPp', params: { id: scope.row.id }}">
                    {{ scope.row.ledger }}
                  </router-link>
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="number" label="Number" align="left" width="200">
              <template slot-scope="scope">
                <el-row type="flex" justify="start">
                  <router-link :to="{name: 'EditPp', params: { id: scope.row.id }}">
                    {{ scope.row.number }}
                  </router-link>
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="customer" label="Customer" align="left" width="200">
              <template slot-scope="scope">
                {{
                  getCustomerById(scope.row.customer) && getCustomerById(scope.row.customer).name
                    ? getCustomerById(scope.row.customer).name
                    : ''
                }}
              </template>
            </el-table-column>
            <el-table-column prop="description" label="Description" align="left" fit></el-table-column>
            <el-table-column prop="amount_decimal" label="Amount" align="right" width="120">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.amount_decimal) }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="200" align="center">
              <template slot-scope="scope">
                <el-row type="flex" justify="center">
                  <el-col v-if="['OPEN', 'AUDITED'].includes(scope.row.status)">
                    <el-button-group>
                      <el-button size="small" plain disabled>
                        {{ scope.row.status }}
                      </el-button>
                      <el-button
                        type="info"
                        size="small"
                        :disabled="checkIfBtnShouldBeDisabled(scope.row.id)"
                        :loading="checkIfBtnShouldBeDisabled(scope.row.id)"
                        @click="handleAuditBtnClick(scope.row, 'Pre Payment')">
                        {{ checkIfBtnShouldBeDisabled(scope.row.id) ? '' : 'Audit' }}
                      </el-button>
                    </el-button-group>
                  </el-col>
                  <el-col v-else-if="scope.row.status === 'DRAFT'">
                    <el-tag type="primary">
                      {{ scope.row.status }}
                    </el-tag>
                  </el-col>
                  <el-col v-else>
                    <el-tag type="gray">
                      {{ scope.row.status }}
                    </el-tag>
                  </el-col>
                </el-row>
              </template>
            </el-table-column>
          </el-table>
        </el-row>
        <el-row v-if="!meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import store from '@/store/store'
  import { payload as ppActionPayload } from '@/components/RouteComponents/Pps/actionPayload'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      ListViewHeader
    },
    mixins: [ listViewMixins ],
    data () {
      return {
        filters: [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: [
                { label: 'DRAFT', value: 'DRAFT' },
                { label: 'OPEN', value: 'OPEN' },
                { label: 'AUDITED', value: 'AUDITED' },
                { label: 'POSTED', value: 'POSTED' },
                { label: 'CLOSED', value: 'CLOSED' },
                { label: 'VOIDED', value: 'VOIDED' },
                { label: 'DELETED', value: 'DELETED' }
              ],
              multi: true,
              placeholder: 'Status'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'vendor',
              remote: true,
              clearable: true,
              action: 'fetchVendorsWithQuery',
              clearAction: 'clearVendorState',
              getter: 'getVendorsBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Customer',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          }
        ],
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        currentPage: 1
      }
    },
    methods: {
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      /**
       * Handles paginating between AP pages
       * @param page  Target page
       */
      async changePage (page) {
        this.changingPage = true
        this.currentPage = page
        const { currentFilters } = this
        try {
          ppActionPayload.page = page
          if (currentFilters) {
            ppActionPayload.query = currentFilters
          }
          await this.$store.dispatch('clearPpState', [])
          const entities = await store.dispatch('fetchByPage', ppActionPayload)
          if (entities) {
            const dependencies = await store.dispatch('findDependencies', entities)
            await Promise.all(dependencies)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.restoreSelection('ppsTable')
        } catch (e) {
          this.changingPage = false
          console.log(e)
        }
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getPpMetadata',
        getApById: 'getPpById',
        getCustomerById: 'getCustomerById',
        selection: 'getSelectedTransactions'
      }),
      pps () {
        return this.$store.getters.getPps.filter(pp => pp.page === this.currentPage)
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('list-view').offsetHeight
        const headerHeight = document.getElementById('list-view-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    /**
     * Checks for page 1 of data. If those results exist, great! The getter should grab them when the route resolves.
     * If they don't exist, fetches them, finds dependencies, and resolves route nav.
     * @param to
     * @param from
     * @param next
     * @return {Promise.<*>}
     */
    async beforeRouteEnter (to, from, next) {
      try {
        ppActionPayload.page = 1
        ppActionPayload.skipCacheCheck = true
        const entities = await store.dispatch('fetchByPage', ppActionPayload)
        if (entities) {
          const dependencies = await store.dispatch('findDependencies', entities)
          await Promise.all(dependencies)
        }
      } catch (e) {
        console.log(e)
      }
      next()
    },
    beforeDestroy () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    }
  }
</script>

<style scoped>
  #list-view-header {
    border-bottom: 1px solid lightgray;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Pps/Pps.vue